import FlatList from "common/Lists/FlatList.tsx";
import React, { useContext } from "react";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import {
  AssetManagerInvoice,
  AssetManagerInvoiceWithKey,
  LanguageNameSpaces,
} from "@shared/types";
import { ColumnType } from "@shared/common/CardsList/types.ts";
import { sortByNumberProperty } from "@shared/utils/general.ts";
import useExpandedCardsSorters from "common/CardSorters/useExpandedCardsSorters.tsx";
import { COLUMN_SPANS } from "features/payouts/Invoices/constants.ts";
import InvoiceCard from "features/payouts/Invoices/InvoiceCard.tsx";
import EmptyView from "common/Empty/EmptyView.tsx";
import { EmptyStatesType } from "common/Icons/types.ts";
import { PageWrapperContext } from "common/Home/PageWrapper/PageWrapper.context.tsx";
import usePageWrapper from "common/Home/PageWrapper/usePageWrapper.ts";
import { checkSearch } from "@shared/utils/misc.ts";
import useAccounts from "features/payouts/Invoices/hooks/useAccounts.ts";
import { useTheme } from "styled-components";

interface InvoicesCardsListProps {
  data?: AssetManagerInvoice[];
  loading: boolean;
  refreshing: boolean;
  onRefresh: () => void;
  topContainer?: React.JSX.Element;
}

const InvoicesCardsList = ({
  data,
  loading,
  refreshing,
  onRefresh,
  topContainer,
}: InvoicesCardsListProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PAYOUTS);
  const { search: mobileSearch } = useContext(PageWrapperContext);
  const { getAccountsNames } = useAccounts();
  const { isNewLook } = useTheme();

  usePageWrapper({
    showSearch: true,
  });

  const filteredInvoices = data
    ?.map((item) => ({ ...item, key: item.id }))
    .filter((invoice) => {
      const accounts = getAccountsNames(invoice.client_ids);
      return accounts.find((account) =>
        checkSearch(account, mobileSearch.value)
      );
    });

  const columns: ColumnType<AssetManagerInvoice>[] = [
    {
      title: t("invoices.table.created_at"),
      key: "created_at",
      span: COLUMN_SPANS[0],
      sorter: (a, b) => sortByNumberProperty(a, b, "created_at"),
    },
    {
      title: t("invoices.table.accounts"),
      key: "clients_ids",
      span: COLUMN_SPANS[1],
    },
    {
      title: t("invoices.table.profit_share"),
      key: "profit_share_amount",
      span: COLUMN_SPANS[2],
      sorter: (a, b) => sortByNumberProperty(a, b, "profit_share_amount"),
    },
  ];

  const { sortedData, cardsSorters, toggleExpanded, isExpanded } =
    useExpandedCardsSorters<AssetManagerInvoiceWithKey>({
      data: filteredInvoices || [],
      columns,
    });

  const renderContent = ({
    item,
    index,
  }: {
    item: AssetManagerInvoiceWithKey;
    index: number;
  }) => {
    return (
      <InvoiceCard
        invoice={item}
        toggleExpanded={toggleExpanded}
        isExpanded={isExpanded(item?.key)}
        isFirst={index === 0}
        isLast={index === (sortedData?.length || 0) - 1}
      />
    );
  };

  return (
    <FlatList
      data={sortedData || []}
      // New look: no gap - the rows read as one panel.
      gap={isNewLook ? 0 : 12}
      refreshing={refreshing}
      onRefresh={onRefresh}
      loading={loading}
      renderContent={renderContent}
      paddingBottom={16}
      renderTopContainer={() => (
        <div style={{ marginBottom: isNewLook ? 12 : 16 }}>
          {topContainer}
          {/* New look: no sort row above an empty list. */}
          {(!isNewLook || !!sortedData?.length) && cardsSorters}
        </div>
      )}
      emptyState={
        <EmptyView
          title={t("invoices.empty.title")}
          iconKey={EmptyStatesType.Invoices}
          iconWidth={100}
        />
      }
    />
  );
};

export default InvoicesCardsList;
